/**
 * Printers Configuration Page
 * 
 * Manage thermal printers and print templates
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { FormModal } from '../../../components/ui/FormModal';
import { DataGrid } from '../../../components/ui/DataGrid';
import { 
  Printer, 
  Plus, 
  Edit, 
  Trash2, 
  TestTube, 
  Wifi, 
  Usb, 
  Bluetooth, 
  Settings,
  FileText,
  Network,
  RefreshCw,
} from 'lucide-react';

// Connection type icons
const connectionIcons = {
  network: Network,
  usb: Usb,
  serial: Usb,
  bluetooth: Bluetooth,
};

// Paper width options
const paperWidthOptions = [
  { value: 58, label: '58mm (Bobina pequena)' },
  { value: 80, label: '80mm (Bobina padrão)' },
];

// Protocol options
const protocolOptions = [
  { value: 'escpos', label: 'ESC/POS (Epson, Elgin, Bematech)' },
  { value: 'star', label: 'Star (Star Micronics)' },
];

// Cut type options
const cutTypeOptions = [
  { value: 'partial', label: 'Guilhotina Parcial' },
  { value: 'full', label: 'Guilhotina Total' },
  { value: 'none', label: 'Sem Corte' },
];

// Use for options
const useForOptions = [
  { value: 'receipt', label: 'Cupom de Venda' },
  { value: 'kitchen', label: 'Comanda de Cozinha' },
  { value: 'report', label: 'Relatórios' },
  { value: 'label', label: 'Etiquetas' },
];

export default function PrintersPage() {
  const { t } = useTranslation();
  const [printers, setPrinters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('printers');
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [testingPrinter, setTestingPrinter] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    model: '',
    connection: {
      type: 'network',
      ip_address: '',
      port: 9100,
      device_path: '',
      baud_rate: 9600,
      bluetooth_address: '',
    },
    settings: {
      paper_width: 80,
      protocol: 'escpos',
      encoding: 'cp860',
      cut_type: 'partial',
      beep_on_print: false,
      open_cash_drawer: false,
    },
    capabilities: {
      can_cut: true,
      can_beep: true,
      has_cash_drawer: false,
      supports_barcode: true,
      supports_qrcode: true,
    },
    is_default: false,
    is_active: true,
    use_for: ['receipt'],
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [printersRes, templatesRes] = await Promise.all([
        api.get('/printers'),
        api.get('/printers/templates'),
      ]);
      setPrinters(printersRes.data);
      setTemplates(templatesRes.data);
    } catch (err) {
      toast.error('Erro ao carregar impressoras');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      model: '',
      connection: {
        type: 'network',
        ip_address: '',
        port: 9100,
        device_path: '',
        baud_rate: 9600,
        bluetooth_address: '',
      },
      settings: {
        paper_width: 80,
        protocol: 'escpos',
        encoding: 'cp860',
        cut_type: 'partial',
        beep_on_print: false,
        open_cash_drawer: false,
      },
      capabilities: {
        can_cut: true,
        can_beep: true,
        has_cash_drawer: false,
        supports_barcode: true,
        supports_qrcode: true,
      },
      is_default: false,
      is_active: true,
      use_for: ['receipt'],
    });
    setEditingPrinter(null);
  };

  // Open modal for new printer
  const handleNewPrinter = () => {
    resetForm();
    setModalOpen(true);
  };

  // Open modal for editing
  const handleEdit = (printer) => {
    setEditingPrinter(printer);
    setFormData({
      name: printer.name || '',
      description: printer.description || '',
      model: printer.model || '',
      connection: printer.connection || { type: 'network' },
      settings: printer.settings || { paper_width: 80 },
      capabilities: printer.capabilities || {},
      is_default: printer.is_default || false,
      is_active: printer.is_active ?? true,
      use_for: printer.use_for || ['receipt'],
    });
    setModalOpen(true);
  };

  // Save printer
  const handleSave = async () => {
    try {
      if (editingPrinter) {
        await api.put(`/printers/${editingPrinter.id}`, formData);
        toast.success('Impressora atualizada!');
      } else {
        await api.post('/printers', formData);
        toast.success('Impressora criada!');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar impressora');
    }
  };

  // Delete printer
  const handleDelete = async () => {
    try {
      await api.delete(`/printers/${deleteId}`);
      toast.success('Impressora removida!');
      setDeleteDialogOpen(false);
      setDeleteId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover impressora');
    }
  };

  // Test print
  const handleTestPrint = async (printer) => {
    try {
      setTestingPrinter(printer.id);
      const response = await api.post(`/printers/${printer.id}/test`);
      toast.success('Teste de impressão enviado!');
      // Here you would typically send the commands to the actual printer
      console.log('Print commands:', response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro no teste de impressão');
    } finally {
      setTestingPrinter(null);
    }
  };

  // Handle modal close
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => resetForm(), 100);
    }
  };

  // Update form field
  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Update nested form field
  const updateNestedForm = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  // Toggle use_for
  const toggleUseFor = (value) => {
    setFormData(prev => ({
      ...prev,
      use_for: prev.use_for.includes(value)
        ? prev.use_for.filter(v => v !== value)
        : [...prev.use_for, value],
    }));
  };

  // Column definitions for printers grid
  const printerColumns = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1.5,
      cellRenderer: (params) => (
        <div className="flex items-center gap-2">
          <Printer className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{params.value}</span>
          {params.data.is_default && (
            <Badge variant="outline" className="text-xs">Padrão</Badge>
          )}
        </div>
      ),
    },
    {
      field: 'model',
      headerName: 'Modelo',
      flex: 1,
    },
    {
      field: 'connection.type',
      headerName: 'Conexão',
      flex: 0.8,
      valueGetter: (params) => params.data.connection?.type,
      cellRenderer: (params) => {
        const type = params.data.connection?.type || 'network';
        const Icon = connectionIcons[type] || Network;
        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            <span className="capitalize">{type}</span>
          </div>
        );
      },
    },
    {
      field: 'settings.paper_width',
      headerName: 'Papel',
      flex: 0.6,
      valueGetter: (params) => params.data.settings?.paper_width,
      cellRenderer: (params) => `${params.value || 80}mm`,
    },
    {
      field: 'use_for',
      headerName: 'Uso',
      flex: 1,
      cellRenderer: (params) => (
        <div className="flex gap-1 flex-wrap">
          {(params.value || []).map(use => (
            <Badge key={use} variant="secondary" className="text-xs">
              {useForOptions.find(o => o.value === use)?.label || use}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      cellRenderer: (params) => (
        <Badge variant={params.data.is_active ? 'default' : 'secondary'}>
          {params.data.is_active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      flex: 1,
      sortable: false,
      cellRenderer: (params) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleTestPrint(params.data)}
            disabled={testingPrinter === params.data.id}
            title="Teste de Impressão"
          >
            {testingPrinter === params.data.id ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <TestTube className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleEdit(params.data)}
            title="Editar"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => {
              setDeleteId(params.data.id);
              setDeleteDialogOpen(true);
            }}
            title="Excluir"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Printer className="h-6 w-6" />
            Configuração de Impressoras
          </h1>
          <p className="text-muted-foreground">
            Gerencie impressoras térmicas e templates de impressão
          </p>
        </div>
        <Button onClick={handleNewPrinter} data-testid="new-printer-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nova Impressora
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="printers" className="flex items-center gap-2">
            <Printer className="h-4 w-4" />
            Impressoras
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* Printers Tab */}
        <TabsContent value="printers" className="mt-4">
          <DataGrid
            rowData={printers}
            columnDefs={printerColumns}
            loading={loading}
            domLayout="autoHeight"
          />
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => (
              <Card key={template.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    {template.is_default && (
                      <Badge>Padrão</Badge>
                    )}
                  </div>
                  <CardDescription>{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">
                      {useForOptions.find(o => o.value === template.type)?.label || template.type}
                    </Badge>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Printer Modal */}
      <FormModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        title={editingPrinter ? 'Editar Impressora' : 'Nova Impressora'}
        description="Configure as opções da impressora térmica"
        size="lg"
        formId="printer"
        entityId={editingPrinter?.id}
        isDirty={false}
        onSave={handleSave}
        confirmText="Salvar"
        cancelText="Cancelar"
      >
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Básico</TabsTrigger>
            <TabsTrigger value="connection">Conexão</TabsTrigger>
            <TabsTrigger value="paper">Papel</TabsTrigger>
            <TabsTrigger value="advanced">Avançado</TabsTrigger>
          </TabsList>

          {/* Basic Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Impressora *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="Ex: Impressora Caixa 01"
                data-testid="printer-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Modelo</Label>
              <Input
                id="model"
                value={formData.model}
                onChange={(e) => updateForm('model', e.target.value)}
                placeholder="Ex: Epson TM-T20, Elgin i9"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => updateForm('description', e.target.value)}
                placeholder="Descrição ou localização"
              />
            </div>

            <div className="space-y-2">
              <Label>Usar para</Label>
              <div className="flex flex-wrap gap-2">
                {useForOptions.map(option => (
                  <Badge
                    key={option.value}
                    variant={formData.use_for.includes(option.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleUseFor(option.value)}
                  >
                    {option.label}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(v) => updateForm('is_default', v)}
                />
                <Label>Impressora Padrão</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => updateForm('is_active', v)}
                />
                <Label>Ativa</Label>
              </div>
            </div>
          </TabsContent>

          {/* Connection Tab */}
          <TabsContent value="connection" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Tipo de Conexão</Label>
              <Select
                value={formData.connection.type}
                onValueChange={(v) => updateNestedForm('connection', 'type', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="network">
                    <div className="flex items-center gap-2">
                      <Network className="h-4 w-4" />
                      Rede (IP/WiFi)
                    </div>
                  </SelectItem>
                  <SelectItem value="usb">
                    <div className="flex items-center gap-2">
                      <Usb className="h-4 w-4" />
                      USB
                    </div>
                  </SelectItem>
                  <SelectItem value="serial">
                    <div className="flex items-center gap-2">
                      <Usb className="h-4 w-4" />
                      Serial (COM)
                    </div>
                  </SelectItem>
                  <SelectItem value="bluetooth">
                    <div className="flex items-center gap-2">
                      <Bluetooth className="h-4 w-4" />
                      Bluetooth
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Network settings */}
            {formData.connection.type === 'network' && (
              <>
                <div className="space-y-2">
                  <Label>Endereço IP</Label>
                  <Input
                    value={formData.connection.ip_address || ''}
                    onChange={(e) => updateNestedForm('connection', 'ip_address', e.target.value)}
                    placeholder="192.168.1.100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Porta</Label>
                  <Input
                    type="number"
                    value={formData.connection.port || 9100}
                    onChange={(e) => updateNestedForm('connection', 'port', parseInt(e.target.value))}
                    placeholder="9100"
                  />
                </div>
              </>
            )}

            {/* Serial settings */}
            {(formData.connection.type === 'serial' || formData.connection.type === 'usb') && (
              <>
                <div className="space-y-2">
                  <Label>Caminho do Dispositivo</Label>
                  <Input
                    value={formData.connection.device_path || ''}
                    onChange={(e) => updateNestedForm('connection', 'device_path', e.target.value)}
                    placeholder="COM3 ou /dev/usb/lp0"
                  />
                </div>
                {formData.connection.type === 'serial' && (
                  <div className="space-y-2">
                    <Label>Baud Rate</Label>
                    <Select
                      value={String(formData.connection.baud_rate || 9600)}
                      onValueChange={(v) => updateNestedForm('connection', 'baud_rate', parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="9600">9600</SelectItem>
                        <SelectItem value="19200">19200</SelectItem>
                        <SelectItem value="38400">38400</SelectItem>
                        <SelectItem value="57600">57600</SelectItem>
                        <SelectItem value="115200">115200</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* Bluetooth settings */}
            {formData.connection.type === 'bluetooth' && (
              <div className="space-y-2">
                <Label>Nome ou Endereço Bluetooth</Label>
                <Input
                  value={formData.connection.bluetooth_address || ''}
                  onChange={(e) => updateNestedForm('connection', 'bluetooth_address', e.target.value)}
                  placeholder="Nome do dispositivo"
                />
              </div>
            )}
          </TabsContent>

          {/* Paper Tab */}
          <TabsContent value="paper" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Largura do Papel</Label>
              <Select
                value={String(formData.settings.paper_width)}
                onValueChange={(v) => updateNestedForm('settings', 'paper_width', parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paperWidthOptions.map(opt => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Protocolo</Label>
              <Select
                value={formData.settings.protocol}
                onValueChange={(v) => updateNestedForm('settings', 'protocol', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {protocolOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Corte</Label>
              <Select
                value={formData.settings.cut_type}
                onValueChange={(v) => updateNestedForm('settings', 'cut_type', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cutTypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Bip ao Imprimir</Label>
                <Switch
                  checked={formData.settings.beep_on_print}
                  onCheckedChange={(v) => updateNestedForm('settings', 'beep_on_print', v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Abrir Gaveta de Dinheiro</Label>
                <Switch
                  checked={formData.settings.open_cash_drawer}
                  onCheckedChange={(v) => updateNestedForm('settings', 'open_cash_drawer', v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Suporta Código de Barras</Label>
                <Switch
                  checked={formData.capabilities.supports_barcode}
                  onCheckedChange={(v) => updateNestedForm('capabilities', 'supports_barcode', v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Suporta QR Code</Label>
                <Switch
                  checked={formData.capabilities.supports_qrcode}
                  onCheckedChange={(v) => updateNestedForm('capabilities', 'supports_qrcode', v)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Tem Gaveta de Dinheiro</Label>
                <Switch
                  checked={formData.capabilities.has_cash_drawer}
                  onCheckedChange={(v) => updateNestedForm('capabilities', 'has_cash_drawer', v)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Encoding</Label>
              <Select
                value={formData.settings.encoding}
                onValueChange={(v) => updateNestedForm('settings', 'encoding', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cp860">CP860 (Português)</SelectItem>
                  <SelectItem value="cp850">CP850 (Europa Ocidental)</SelectItem>
                  <SelectItem value="cp437">CP437 (DOS EUA)</SelectItem>
                  <SelectItem value="utf-8">UTF-8</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>
        </Tabs>
      </FormModal>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Impressora</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover esta impressora? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
